import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from './auth/AuthProvider';
import { AppShell } from './components/AppShell';
import { Wordmark } from './components/Clapper';
import { AppLink, useGo, useRouteParams } from './components/Nav';
import { Button, Spinner } from './components/ui';
import { LoginPage, SignupPage } from './routes/AuthPages';
import { DashboardPage } from './routes/DashboardPage';
import { InvitePage } from './routes/InvitePage';
import { LinksPage } from './routes/LinksPage';
import { NewPage } from './routes/NewPage';
import { OrgBillingPage } from './routes/OrgBillingPage';
import { OrgMembersPage } from './routes/OrgMembersPage';
import { OrgNewPage } from './routes/OrgNewPage';
import { OrgPage } from './routes/OrgPage';
import { RequestPage } from './routes/RequestPage';
import { SettingsPage } from './routes/SettingsPage';
import { SharePage } from './routes/SharePage';

/**
 * Routes, defined in code. No generated route tree and no file-based
 * convention: the app has thirteen routes, and a hand-written tree you can
 * read top to bottom beats a build step.
 *
 * Path params are read with `useRouteParams()` (src/components/Nav.tsx) rather
 * than each route's own `useParams`, which would make every route object's
 * type depend on its own component and its component depend on the route.
 */

function FullPage({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-slate-950 px-6 text-center">
      {children}
    </div>
  );
}

function Restoring() {
  return (
    <FullPage>
      <Spinner size={22} className="text-chalk-faint" />
    </FullPage>
  );
}

function NotFound() {
  return (
    <FullPage>
      <Wordmark />
      <h1 className="text-h2 text-chalk">There is nothing at this address</h1>
      <p className="max-w-sm text-body text-chalk-dim">
        The link may be mistyped, or whatever was here has been deleted.
      </p>
      <AppLink to="/">
        <Button variant="primary">Go to your library</Button>
      </AppLink>
    </FullPage>
  );
}

/**
 * Everything behind an account. Bounces to /login once we know there is none.
 *
 * `chrome` is how a route opts out of the app shell. Exactly one does: /new is
 * a full screen mode rather than a page, and a nav bar offering to take you
 * somewhere else while you are mid-take is the opposite of what that route is
 * for.
 */
function RequireAuth({ children, chrome = true }: { children: ReactNode; chrome?: boolean }) {
  const { user, loading } = useAuth();
  const go = useGo();

  useEffect(() => {
    if (!loading && !user) go('/login', { replace: true });
  }, [go, loading, user]);

  if (loading || !user) return <Restoring />;
  return chrome ? <AppShell>{children}</AppShell> : <>{children}</>;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// ---- public ---------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});

const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/v/$shareId',
  component: function ShareRoute() {
    const { shareId } = useRouteParams();
    return shareId ? <SharePage shareId={shareId} /> : <NotFound />;
  },
});

const requestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$token',
  component: function RequestRoute() {
    const { token } = useRouteParams();
    return token ? <RequestPage token={token} /> : <NotFound />;
  },
});

// The invite page has to work for somebody with no account yet, so it handles
// its own signed-out state instead of sitting behind RequireAuth.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: function InviteRoute() {
    const { token } = useRouteParams();
    return token ? <InvitePage token={token} /> : <NotFound />;
  },
});

// ---- signed in ------------------------------------------------------------

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: function DashboardRoute() {
    return (
      <RequireAuth>
        <DashboardPage />
      </RequireAuth>
    );
  },
});

const newRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/new',
  component: function NewRoute() {
    return (
      <RequireAuth chrome={false}>
        <NewPage />
      </RequireAuth>
    );
  },
});

const linksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/links',
  component: function LinksRoute() {
    return (
      <RequireAuth>
        <LinksPage />
      </RequireAuth>
    );
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: function SettingsRoute() {
    return (
      <RequireAuth>
        <SettingsPage />
      </RequireAuth>
    );
  },
});

// `/org/new` is declared before `/org/$slug`; static segments outrank params
// in the matcher regardless, but the ordering documents the intent.
const orgNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/new',
  component: function OrgNewRoute() {
    return (
      <RequireAuth>
        <OrgNewPage />
      </RequireAuth>
    );
  },
});

const orgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug',
  component: function OrgRoute() {
    const { slug } = useRouteParams();
    return <RequireAuth>{slug ? <OrgPage slug={slug} /> : <NotFound />}</RequireAuth>;
  },
});

const orgMembersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug/members',
  component: function OrgMembersRoute() {
    const { slug } = useRouteParams();
    return <RequireAuth>{slug ? <OrgMembersPage slug={slug} /> : <NotFound />}</RequireAuth>;
  },
});

const orgBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/org/$slug/billing',
  component: function OrgBillingRoute() {
    const { slug } = useRouteParams();
    return <RequireAuth>{slug ? <OrgBillingPage slug={slug} /> : <NotFound />}</RequireAuth>;
  },
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  loginRoute,
  signupRoute,
  newRoute,
  linksRoute,
  settingsRoute,
  shareRoute,
  requestRoute,
  inviteRoute,
  orgNewRoute,
  orgRoute,
  orgMembersRoute,
  orgBillingRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFound,
});
